import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import cn from 'classnames'
import moment from 'moment'
import AnimateHeight from 'react-animate-height'

import Custom404 from './404'
import Custom500 from './500'
import useWindowDimensions from '@hooks/useWindowDimensions';
import ColorUtils from '@utils/ColorUtils'
import NumberUtils from '@utils/NumberUtils'
import DynamoDB from '@lib/DynamoDB'
import Magpie from '@lib/Magpie'
import PaymentDetailsType from 'types/PaymentDetailsType'
import api from 'api'
import { LinkDbType, PaymentLinkItemType, PaymentLinkType } from 'types/PaymentLinkType'
import { HeadersType } from 'types/ApiType'
import { BrandingType } from 'types/MerchantType'
import { SessionItemType, SessionResponseType } from 'types/PaymentSessionType'


export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const LINKS_BASE_URL = process.env.PAYMENT_LINK_URL || 'https://buy.magpie.im';

  let data: PaymentLinkType | null = null;
  console.log('query', query);
  try {
    if (query.linkId) {
      const result: LinkDbType | null | undefined = await DynamoDB.getLinkByUrlKey(query.linkId as string);
      console.log('result', result);
      if (result) {
        const headers: HeadersType = {
          'Content-Type': 'application/json',
          Authorization: `Basic ${Buffer.from(`${result.merchantKey}:`).toString('base64')}`
        };

        const merchantRes = await Magpie.getMerchant(headers);
        if (merchantRes.status === 200) {
          console.log('merchant', merchantRes.data);
          const merchantData = merchantRes.data;
          const merchant = {
            id: merchantData.id,
            name: merchantData.title,
            support_email: merchantData.metadata ? merchantData.metadata.support_email || null : null,
            support_phone: merchantData.metadata ? merchantData.metadata.support_phone || null : null,
          }

          const merchantBranding: BrandingType = {
            icon: merchantData.branding ? (merchantData.branding.icon ? merchantData.branding.icon : null) : null,
            logo: merchantData.branding ? (merchantData.branding.logo ? merchantData.branding.logo : null) : null,
            use_logo: merchantData.branding ? (merchantData.branding.use_logo ? merchantData.branding.use_logo : false) : false,
            primary_color: merchantData.branding ? (merchantData.branding.brand_color ? merchantData.branding.brand_color : null) : null,
            secondary_color: merchantData.branding ? (merchantData.branding.accent_color ? merchantData.branding.accent_color : null) : null
          }

          const today = moment().format('MM/DD/YYYY');
          if (today === result.expiry) {
            console.log('link has expired');
            const expired: LinkDbType = await DynamoDB.deactivateLink(result.id);
            data = {
              id: expired.id,
              object: 'payment.link',
              active: expired.active,
              allow_adjustable_quantity: expired.allowAdjustableQuantity,
              branding: expired.branding ? expired.branding : merchantBranding,
              created: expired.createdAt,
              currency: expired.currency,
              description: expired.description,
              expiry: expired.expiry,
              internal_name: expired.internalName,
              line_items: expired.lineItems,
              livemode: expired.livemode,
              maximum_payments: expired.maximumPayments,
              merchant: merchant,
              merchant_key: expired.merchantKey,
              metadata: expired.metadata,
              payment_method_types: expired.paymentMethodTypes,
              phone_number_collection: expired.phoneNumberCollection,
              redirect_url: expired.redirectUrl,
              shipping_address_collection: expired.shippingAddressCollection,
              updated: expired.lastUpdated,
              url: `${LINKS_BASE_URL}/${expired.urlKey}`
            };
          } else {
            data = {
              id: result.id,
              object: 'payment.link',
              active: result.active,
              allow_adjustable_quantity: result.allowAdjustableQuantity,
              branding: result.branding ? result.branding : merchantBranding,
              created: result.createdAt,
              currency: result.currency,
              description: result.description,
              expiry: result.expiry,
              internal_name: result.internalName,
              line_items: result.lineItems,
              livemode: result.livemode,
              maximum_payments: result.maximumPayments,
              merchant: merchant,
              merchant_key: result.merchantKey,
              metadata: result.metadata,
              payment_method_types: result.paymentMethodTypes,
              phone_number_collection: result.phoneNumberCollection || false,
              redirect_url: result.redirectUrl || null,
              shipping_address_collection: result.shippingAddressCollection,
              updated: result.lastUpdated,
              url: `${LINKS_BASE_URL}/${result.urlKey}`
            };
          }
        } else {
          console.log('error', `No merchant found for key: ${result.merchantKey}`);
          data = null;
        }
      }
    }
  } catch (e) {
    console.error(e);
  }

  console.log('data', data);

  return {
    props: { 
      data
    }, // will be passed to the page component as props
  };
}

type HomePropsType = {
  data: PaymentLinkType | null
}

type ItemType = {
  image?: string | null
  amount: number
  name: string
  description?: string | null
  quantity: string
  remaining?: number | null
}

const Home = ({ data }: HomePropsType): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const windowDimensions = useWindowDimensions();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [containerTop, setContainerTop] = useState<number>(0);
  const [id, setId] = useState<string>();
  const [secretKey, setSecretKey] = useState<string>();
  const [brandColor, setBrandColor] = useState<string>('rgb(254, 254, 254');
  const [accentColor, setAccentColor] = useState<string>('rgb(0, 115, 212)');
  const [textColor, setTextColor] = useState<string>('rgb(93, 93, 93)');
  const [buttonTextColor, setButtonTextColor] = useState<string>('rgb(255, 255, 255)');
  const [useLogo, setUseLogo] = useState<boolean>(false);
  const [logo, setLogo] = useState<string>();
  const [icon, setIcon] = useState<string | null>(null);
  const [allowQuantityChange, setAllowQuantityChange] = useState<boolean>(false);
  const [items, setItems] = useState<ItemType[]>([]);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [shippingFee, setShippingFee] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsType | null>(null);
  const [error, setError] = useState<string>();
  const [pageError, setPageError] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [complete, setComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!pageLoading && windowDimensions && containerRef.current) {
      if (windowDimensions.width < 768) {
        setIsMobile(true);
        setContainerTop(0);
      } else {
        setIsMobile(false);
        const height = containerRef.current.clientHeight;
        const marginTop = (windowDimensions.height - height) / 2;
        setContainerTop(marginTop > 0 ? marginTop : 0);
      }
    }
  }, [containerRef, windowDimensions, pageLoading]);

  useEffect(() => {
    if (data) {

      setId(data.id);
      setAllowQuantityChange(data.allow_adjustable_quantity);
      setSecretKey(data.merchant_key);

      if (data.branding) {
        const {branding} = data;
        if (branding.primary_color) {
          setBrandColor(ColorUtils.hex2rgba(branding.primary_color));
          if (ColorUtils.isDark(branding.primary_color)) {
            setTextColor(ColorUtils.hex2rgba('#FFFFFF'));
          } else {
            setTextColor(ColorUtils.hex2rgba('#5d5d5d'));
          }
        }

        if (branding.secondary_color) {
          setAccentColor(ColorUtils.hex2rgba(branding.secondary_color));
          if (ColorUtils.isDark(branding.secondary_color)) {
            setButtonTextColor(ColorUtils.hex2rgba('#FFFFFF'));
          } else {
            setButtonTextColor(ColorUtils.darker(branding.secondary_color, 70));
          } 
        }

        setIcon(branding.icon);
        if (branding.use_logo && branding.logo) {
          setUseLogo(branding.use_logo);
          setLogo(branding.logo);
        } else if (branding.icon) {
          setLogo(branding.icon);
        } else {
          setLogo('https://client-objects.s3-us-west-2.amazonaws.com/storeicon.png');
        }
      }

      if (data.line_items) {
        
        const lineItems: ItemType[] = [];
        data.line_items.forEach((i) => {
          lineItems.push({
            image: i.image,
            amount: i.amount,
            name: i.name,
            description: i.description,
            quantity: i.quantity !== null ? i.remaining !== undefined ? i.remaining === null ? '1' : i.remaining > 0 ? '1' : '0' : '0' : '1',
            remaining: i.remaining
          })
        });
        setItems(lineItems);
      }
      

      setPageLoading(false);
      setComplete(true);
    }
  }, [data]);

  useEffect(() => {
    if (items.length > 0) {
      
      let t = 0;
      for (const item of items) {
        t += ((parseInt(item.quantity) || 0) * item.amount);
      }

      setSubTotal(t);
      setTotal(t);

      if (t > 0) {
        setComplete(true);
      } else {
        setComplete(false);
      }
    }
  }, [items])

  const hasImages = (): boolean => {
    if (data) {
      if (data.line_items) {
        const itemsWithImages = data.line_items.filter((item) => item.image !== null && item.image !== undefined);
        if (itemsWithImages.length > 0) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  const onSubtractQty = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number, item: ItemType) => {
    e.preventDefault();
    try {
      if ((parseInt(item.quantity) || 0) > 0) {
        const itemsArr = items;
        itemsArr[index].quantity = ((parseInt(item.quantity) || 0) - 1).toString();
        setItems([...itemsArr]);
      }
    } catch (e) {
      console.log('error', e)
    }
  }

  const onAddQty = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number, item: ItemType) => {
    e.preventDefault();
    try {
      if (item.remaining !== undefined) {
        if (item.remaining !== null) {
          if ((parseInt(item.quantity) || 0) < item.remaining) {
            const itemsArr = items;
            itemsArr[index].quantity = ((parseInt(item.quantity) || 0) + 1).toString();
            setItems([...itemsArr]);
          }
        } else {
          //item has infinite stocks
          if (parseInt(item.quantity) < 99) {
            const itemsArr = items;
            itemsArr[index].quantity = ((parseInt(item.quantity) || 0) + 1).toString();
            setItems([...itemsArr]);
          }
        }
      }
    } catch (e) {
      console.log('error', e);
    }
  }

  const onQtyChanged = (e: React.ChangeEvent<HTMLInputElement>, index: number, item: ItemType) => {
    if (e.target.value.length > 0) {
      try {
        const value = parseInt(e.target.value);
        
        if (item.remaining !== undefined) {
          if (item.remaining !== null) {
            if (value <= item.remaining) {
              const itemsArr = items;
              itemsArr[index].quantity = value.toString();
              setItems([...itemsArr]);
            }
          } else {
            if (value < 100) {
              const itemsArr = items;
              itemsArr[index].quantity = value.toString();
              setItems([...itemsArr]);
            }
          }
        }
      } catch (e) {
        console.log('error', e);
      }
    } else {
      const itemsArr = items;
      itemsArr[index].quantity = '';
      setItems([...itemsArr]);
    }
  }

  const onPayClicked = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setError(undefined);
    setLoading(true);
    if (total > 0) {
      console.log('key', secretKey);
      console.log('id', id);
      console.log('items', items.length);
      if (secretKey && id && items.length > 0) {
        console.log('generating line items...', items.length);
        const lineItems: SessionItemType[] = [];
        for (const item of items) {
          if ((parseInt(item.quantity) || 0) > 0) {
            lineItems.push({
              name: item.name,
              description: item.description,
              image: item.image,
              amount: item.amount,
              quantity: parseInt(item.quantity)
            });
          }
        }
        console.log('items', lineItems);

        const client = await api.fetchClientInfo();
        console.log('client', client);

        const response = await api.payLink(secretKey, id, lineItems, client);
        console.log('session', response.data);

        if (response.ok) {
          const session: SessionResponseType = response.data;
          if (session.payment_url) {
            window.location.href = session.payment_url;
          } else {
            setLoading(false);
            setError('Unable to process this transaction at this time.');
          }
        } else {
          setLoading(false);
          setError(response.data.message || 'Unable to process this transaction at this time.');
        }
      }
    } else {
      setLoading(false);
      setError('Please add an item you want to purchase.');
    }
    
  }

  if (!data) {
    return <Custom404 />;
  }

  if (pageError) {
    return <Custom500 />;
  }

  return (
    <div>
      <Head>
        <title>{pageLoading || !data.merchant ? 'Magpie Payment Link' : `${data.merchant.name} - Payment Link`}</title>
        <link rel="icon" href={icon || '/favicon.ico'} />
      </Head>
      
      <div className="flex flex-row flex-nowrap overflow-x-hidden md:overflow-x-auto">
        <div className="flex-initial min-w-0 max-w-full basis-0 flex-grow">
          <div ref={containerRef} className="flex flex-col flex-nowrap items-center w-full md:max-w-2xl md:mx-auto md:mb-12" style={{marginTop: `${containerTop}px`}}>
            {/* App background */}
            <div className="fixed inset-0 -z-1 min-h-screen" style={{backgroundColor: isMobile || pageLoading || !data.active ? 'white' : brandColor}}></div>

            {pageLoading ? (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="spinner-icon">
                    <div className="w-12 h-12">
                      <svg
                        className="w-12 h-12"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        focusable="false"
                      >
                        <ellipse
                          className="origin-center animate-spinner-spin"
                          cx="12"
                          cy="12"
                          rx="10"
                          ry="10"
                          style={{
                            fill: 'transparent',
                            stroke: 'rgb(212, 212, 216)',
                            strokeLinecap: 'round',
                            strokeDasharray: 70,
                            strokeDashoffset: 30,
                            strokeWidth: 1.3,
                          }}
                        ></ellipse>
                      </svg>
                    </div>
                  </div>
                </div>
              ) : data.active ? (
                <>
                  {/* Header */}
                  <div className="flex flex-nowrap flex-row justify-between items-center w-full p-4 bg-white transition-colors duration-150 ease-in md:w-auto md:p-0 md:bg-transparent md:self-start md:my-6" style={ isMobile ? {backgroundColor: brandColor} : {}}>
                    <div className="flex flex-nowrap flex-row justify-between items-center">
                      {
                        logo &&
                        <img className="max-w-37.5 max-h-8 shadow-header-image rounded-full" src={logo} alt={data.merchant?.name} />
                      }
                      {
                        !useLogo ? (
                          <div className={cn("m-0 ml-3", {
                            "text-sm": isMobile,
                            "text-2xl": !logo && !isMobile,
                            "text-base": !!logo && !isMobile,
                          })} style={{color: textColor}}>
                            {data.merchant?.name}
                          </div>
                        ) : null
                      }
                      
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex flex-nowrap flex-col z-1 w-full md:-m-2 md:animate-enter-background-shadow md:animate-fill-both bg-transparent">
                    <div className="flex-initial min-w-0 max-w-full w-auto basis-auto md:p-2 md:px-0">
                      <div className="bg-white p-0 md:p-8 md:rounded-md md:shadow-card">
                        <div className="flex flex-nowrap flex-initial min-w-0 max-w-full flex-col basis-0 flex-grow pt-5 px-4 pb-4 md:px-0 md:pb-0 md:pt-2">
                          <table className="w-full">
                            <tbody>
                              <tr>
                                <td colSpan={2} className="align-top">
                                  <div className="mt-2">
                                    <div className="min-h-px overflow-auto flex-grow">
                                      <div className="items-list min-h-0 p-px overflow-y-auto">
                                        {items.map((item, index) => (
                                          <div key={index} className="py-6 flex text-sm shadow-light-bottom-1 sm:items-center first:pt-0">
                                            {hasImages() ? (
                                                <div className="flex-shrink-0 w-10.5 h-10.5 sm:w-24 sm:h-24">
                                                  <img className={cn("w-full h-full rounded object-cover", {
                                                    "shadow-keyline": item.image
                                                  })} src={item.image || ''} alt='' />
                                                </div>
                                              ) : null
                                            }
                                            <div className="ml-4 flex-auto grid gap-y-3 gap-x-5 grid-rows-1 grid-cols-1 items-start sm:ml-6 sm:flex sm:gap-0">
                                              <div className="flex-auto row-end-1 sm:pr-6">
                                                <span className="m-0 text-sm text-gray-900 font-medium break-words proportional-nums whitespace-normal">{item.name}</span>
                                                <p className="mt-1 text-xs text-gray-400 proportional-nums whitespace-normal">{item.description || ''}</p>
                                                <div className="min-h-5 flex-grow" />
                                                {item.remaining !== undefined ? (
                                                  <div className="flex-1 flex items-end justify-between">
                                                    <p className={cn("flex items-center text-xs proportional-nums whitespace-normal", {
                                                      "text-yellow-400": item.remaining !== null && item.remaining < 10,
                                                      "text-green-500": item.remaining === null || item.remaining > 9,
                                                      "text-red-500": item.remaining !== null && item.remaining < 1
                                                    })}>
                                                      <span>{item.remaining !== null ? item.remaining > 9 ? 'In stock' : item.remaining < 1 ? 'Out of stock' : `${item.remaining} ${item.remaining > 1 ? 'stocks' : 'stock'} left` : 'In stock'}</span>
                                                    </p>
                                                  </div>
                                                ) : null}
                                              </div>
                                              <p className="row-end-2 row-span-2 text-sm font-medium text-gray-900 tabular-nums tracking-numbers whitespace-nowrap sm:ml-5 sm:order-1 sm:flex-none sm:w-1/5 sm:text-right">{NumberUtils.formatCurrency(item.amount / 100, data.currency)}</p>
                                              {allowQuantityChange ? item.remaining !== undefined ? item.remaining !== 0 ? (
                                                <div className="flex flex-nowrap justify-start items-center">
                                                  <label htmlFor={`quantity-${index}`} className="sr-only">
                                                    Quantity, {item.name}
                                                  </label>
                                                  <button disabled={item.remaining === 0} onClick={(e) => onSubtractQty(e, index, item)} className={cn("p-2 bg-black bg-opacity-3 rounded-full mr-4 transition-qty-btn focus:outline-none", {
                                                    "cursor-not-allowed opacity-50 transform-none": ((parseInt(item.quantity) || 0) < 1) || item.remaining === 0,
                                                    "cursor-pointer hover:bg-opacity-10 focus:ring focus:ring-magpie-400 focus:ring-opacity-50 active:transform active:scale-98": (parseInt(item.quantity) || 0) > 0
                                                  })}>
                                                    <div className="flex flex-nowrap justify-center items-center">
                                                      <svg className="h-3 w-3 mr-0 flex-shrink-0 text-gray-600" focusable="false" fill="currentColor" width="12" height="3" viewBox="0 0 13 2"><rect y="0.75" width="12" height="1.5" rx="0.75" fillOpacity="0.9"></rect></svg>
                                                      <span className="m-0 font-medium truncate"></span>
                                                    </div>
                                                  </button>
                                                  <div className="relative">
                                                    <span className="relative block m-0 p-0">
                                                      <input disabled={item.remaining === 0} className={cn("relative h-8 px-3 py-1 text-sm leading-5 border-0 shadow-input transition-qty-input appearance-none w-11 text-center rounded-md focus:ring focus:ring-magpie-400 focus:ring-opacity-50 focus:outline-none", {
                                                        "text-gray-900 bg-white": item.remaining === null || (item.remaining !== null && item.remaining > 0),
                                                        "text-gray-500 bg-gray-50": item.remaining === 0
                                                      })} autoComplete="fake" autoCorrect="off" spellCheck="false" id={`quantity-${index}`} name={`quantity-${index}`} type="text" inputMode="numeric" value={item.quantity} onChange={(e) => onQtyChanged(e, index, item)} />
                                                    </span>
                                                  </div>
                                                  <button disabled={item.remaining === 0} onClick={(e) => onAddQty(e, index, item)} className={cn("p-2 bg-black bg-opacity-3 rounded-full ml-4 transition-qty-btn focus:outline-none", {
                                                    "cursor-not-allowed opacity-50 transform-none": item.remaining !== null && (parseInt(item.quantity) || 0) >= item.remaining,
                                                    "cursor-pointer hover:bg-opacity-10 focus:ring focus:ring-magpie-400 focus:ring-opacity-50 active:transform active:scale-98": item.remaining !== null && (parseInt(item.quantity) || 0) < item.remaining
                                                  })}>
                                                    <div className="flex flex-nowrap justify-center items-center">
                                                      <svg className="h-3 w-3 mr-0 flex-shrink-0 text-gray-600" focusable="false" fill="currentColor" width="12" height="13" viewBox="0 0 12 13"><path fillRule="evenodd" clipRule="evenodd" d="M6.75 5.75H11.25C11.6642 5.75 12 6.08579 12 6.5C12 6.91421 11.6642 7.25 11.25 7.25H6.75V11.75C6.75 12.1642 6.41421 12.5 6 12.5C5.58579 12.5 5.25 12.1642 5.25 11.75V7.25H0.75C0.335786 7.25 0 6.91421 0 6.5C0 6.08579 0.335786 5.75 0.75 5.75H5.25V1.25C5.25 0.835786 5.58579 0.5 6 0.5C6.41421 0.5 6.75 0.835786 6.75 1.25V5.75Z" fillOpacity="0.9"></path></svg>
                                                      <span className="m-0 font-medium truncate"></span>
                                                    </div>
                                                  </button>
                                                </div>
                                              ) : null : null : null}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="mt-4 sm:mt-6">
                                        <div className="bg-gray-50 p-4 sm:p-6 sm:rounded-md">
                                          <h2 className="sr-only">
                                            Order summary
                                          </h2>
                                          <div className="flow-root">
                                            <dl className="-my-4 text-sm">
                                              <div className="py-4 flex items-center justify-between shadow-light-bottom-1">
                                                <dt className="text-gray-600 text-sm font-normal proportional-nums whitespace-normal">Subtotal</dt>
                                                <dd className="font-medium text-gray-900 tabular-nums whitespace-nowrap">{NumberUtils.formatCurrency(subTotal / 100, data.currency)}</dd>
                                              </div>
                                              <div className="py-4 flex items-center justify-between">
                                                <dt className="text-base font-medium text-gray-900">Order total</dt>
                                                <dd className="text-base font-medium text-gray-900">{NumberUtils.formatCurrency(total / 100, data.currency)}</dd>
                                              </div>
                                            </dl>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <AnimateHeight
                            height={error ? "auto" : 0}
                            className="pointer-events-none transition-height duration-250 ease-in-out"
                            animateOpacity
                          >
                            <div className="pt-2">
                              <div className="flex flex-col justify-start flex-nowrap -ml-1 -mt-1">
                                <div className="ml-1 mt-1 pointer-events-auto bg-red-100 rounded-md">
                                  <div
                                    role="alert"
                                    className="flex flex-row items-center leading-5 p-4"
                                  >
                                    <div className="pr-2 text-red-500">
                                      <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        ></path>
                                      </svg>
                                    </div>
                                    <span className="inline text-sm text-red-500 proportional-nums whitespace-normal -mt-px">
                                      {error}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </AnimateHeight>
                          <div className="flex flex-row -m-2 flex-wrap pt-5">
                            <div className="flex-initial min-w-0 max-w-full w-full p-2">
                              <button onClick={onPayClicked} className="relative overflow-hidden h-12 w-full mt-3 p-0 text-white shadow-button border-0 outline-none rounded-md cursor-pointer transition-button backface-hidden transform translate-z-0 focus:outline-none focus:ring-0" style={{ backgroundColor: accentColor, color: buttonTextColor }}>
                                <div className={cn("content-empty absolute top-0 bottom-0 left-0 h-full w-3/2 transform -translate-x-3/2 transition-opacity duration-200 ease-in", {
                                  "opacity-0": !complete || loading,
                                  "opacity-100 animate-shimmer-move": complete && !loading
                                })} style={{ background: `linear-gradient(to right, ${ColorUtils.changeAlpha(accentColor, 0)} 0%, ${ColorUtils.brighter(accentColor, 10)} 50%, ${ColorUtils.changeAlpha(accentColor, 0)} 100%)` }}></div>
                                <div>
                                  <span className={cn("m-0 font-medium absolute top-0 left-0 w-full text-center leading-12 transition-all duration-200 ease-in", {
                                    "opacity-0 transform -translate-x-4": loading
                                  })}>
                                    Continue to payment
                                  </span>
                                  <span className={cn("m-0 font-medium truncate absolute top-0 left-0 w-full text-center leading-12 transition-all duration-200 ease-in", {
                                    "opacity-0 transform translate-x-4": !loading,
                                    "opacity-100 transform translate-x-0": loading
                                  })}>Loading...</span>
                                </div>
                                <div className="absolute top-0 left-0 h-full w-full transform -translate-x-4 transition-transform duration-300 ease-in">
                                  <div className={cn("flex absolute top-1/2 right-0 transform -translate-y-1/2 transition-all duration-200 ease-in", {
                                    "opacity-0 transform -translate-y-1/2 scale-90": !loading,
                                    "opacity-100": loading
                                  })}>
                                    <div className="w-5 h-5">
                                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false"><ellipse className="spinner-ellipse animate-spinner-spin" cx="12" cy="12" rx="10" ry="10" style={{stroke: buttonTextColor}}></ellipse></svg>
                                    </div>
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                          {data.merchant ? (<div className="m-0 text-sm font-medium text-gray-400 text-center pt-5 transition-all duration-200 ease-in">
                            {`Questions? `}
                            {
                              data.merchant.support_email ? (
                                <Link href={`mailto:${data.merchant.support_email}`} passHref>
                                  <a className="no-underline cursor-pointer focus:outline-none focus:ring-0" style={{ color: accentColor }}>
                                    {`Contact ${data.merchant.name}`}
                                  </a>
                                </Link>
                              ) : (
                                <span className="inline text-gray-600 font-medium proportional-nums whitespace-nowrap">
                                  <span>{`Contact ${data.merchant.name}`}</span>
                                </span>
                              )
                            }
                          </div>) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Footer */}
                  <div className="flex-initial min-w-0 max-w-full basis-0 flex-grow mt-4 md:mt-8">
                    <footer className="flex flex-col items-center md:h-4 md:leading-4 md:flex-row">
                      <div className="w-full text-center p-1 md:text-left md:w-auto md:p-0 md:pr-4 md:border-r md:border-gray-200">
                        <Link href="https://magpie.im" passHref>
                          <a className="bg-transparent no-underline cursor-pointer text-blue-500 focus:outline-none focus:ring-0" target="_blank" rel="noopener">
                            <span className={cn("m-0 text-xs font-normal", {
                              "text-gray-400": isMobile || !ColorUtils.isDark(brandColor),
                              "text-isDark-gray-500": !isMobile && ColorUtils.isDark(brandColor)
                            })}>
                              {`Powered by `}
                              <svg className={cn("inline ml-0.5 h-4 poweredby-icon", { "isDark": !isMobile && ColorUtils.isDark(brandColor) })} width="56px" height="14px" viewBox="0 0 56 14" xmlns="http://www.w3.org/2000/svg">
                              <g stroke="none" strokeWidth="1" fillRule="evenodd">
                                <g id="svg_logo" transform="translate(0.000000, -1.000000)">
                                    <path d="M17.8374331,11.9805195 L17.8374331,8.415646 C17.8374331,8.0773131 17.8745672,7.76579927 17.9488354,7.48110451 C18.0231036,7.19640975 18.1365689,6.9488491 18.2892313,6.73842254 C18.4418937,6.52799598 18.6399422,6.36295554 18.8833769,6.24330122 C19.1268115,6.1236469 19.4135693,6.06381974 19.7436502,6.06381974 C19.9912108,6.06381974 20.2016374,6.10507985 20.3749299,6.18760007 C20.5482223,6.27012029 20.6864437,6.38358559 20.789594,6.52799598 C20.8927442,6.67240636 20.9670124,6.84157281 21.0123986,7.03549533 C21.0577847,7.22941784 21.0804777,7.43365539 21.0804777,7.64820796 L21.0804777,11.9805195 L21.8602938,11.9805195 L21.8602938,8.36613387 C21.8602938,8.06906108 21.8871129,7.78230331 21.940751,7.50586058 C21.9943892,7.22941784 22.0872244,6.98392019 22.2192568,6.76936762 C22.3512891,6.55481505 22.5287076,6.38358559 22.7515122,6.25567925 C22.9743168,6.12777291 23.2548855,6.06381974 23.5932184,6.06381974 C24.1543559,6.06381974 24.546327,6.20204111 24.7691316,6.47848384 C24.9919361,6.75492658 25.1033384,7.15721265 25.1033384,7.68534206 L25.1033384,11.9805195 L25.8831545,11.9805195 L25.8831545,7.64820796 C25.8831545,6.15459198 25.1776066,5.407784 23.7665109,5.407784 C23.3456578,5.407784 22.9495607,5.51093427 22.5782197,5.71723482 C22.2068787,5.92353537 21.92631,6.2371122 21.7365135,6.65796532 C21.6209852,6.2371122 21.3919916,5.92353537 21.0495327,5.71723482 C20.7070737,5.51093427 20.3254177,5.407784 19.9045646,5.407784 C19.3846872,5.407784 18.9576451,5.51918629 18.6234382,5.74199089 C18.2892313,5.96479548 18.0189776,6.2742463 17.812677,6.67034336 L17.775543,6.67034336 L17.775543,5.59345449 L17.057617,5.59345449 L17.057617,11.9805195 L17.8374331,11.9805195 Z M29.1633332,12.1538119 C29.4769101,12.1538119 29.7471638,12.1249299 29.9740944,12.0671657 C30.201025,12.0094016 30.4031995,11.9268813 30.580618,11.8196051 C30.7580365,11.7123288 30.9189509,11.5782334 31.0633613,11.417319 C31.2077717,11.2564046 31.3501191,11.0727971 31.4904034,10.8664965 L31.5151595,10.8664965 C31.5151595,11.031537 31.5254745,11.1821364 31.5461046,11.3182947 C31.5667346,11.4544531 31.6100578,11.5720444 31.6760739,11.6710687 C31.7420901,11.7700929 31.8349253,11.8464241 31.9545797,11.9000623 C32.074234,11.9537004 32.2330854,11.9805195 32.4311339,11.9805195 C32.5384102,11.9805195 32.6271195,11.9763935 32.6972616,11.9681414 C32.7674038,11.9598894 32.847861,11.9475114 32.9386333,11.9310073 L32.9386333,11.2749716 C32.8643651,11.3079797 32.7818449,11.3244837 32.6910726,11.3244837 C32.3857478,11.3244837 32.2330854,11.1676953 32.2330854,10.8541185 L32.2330854,7.46253746 C32.2330854,7.03343232 32.1629432,6.68478439 32.0226588,6.41659368 C31.8823745,6.14840297 31.70083,5.94003941 31.4780254,5.79150302 C31.2552208,5.64296662 31.0035341,5.54187935 30.7229654,5.48824121 C30.4423966,5.43460307 30.1618279,5.407784 29.8812592,5.407784 C29.5099182,5.407784 29.1715853,5.44904411 28.8662605,5.53156433 C28.5609356,5.61408455 28.2948079,5.74199089 28.0678773,5.91528335 C27.8409467,6.08857581 27.6614652,6.3113804 27.5294329,6.58369712 C27.3974005,6.85601385 27.3190063,7.17784271 27.2942503,7.54918369 L28.0740663,7.54918369 C28.0905704,7.02930631 28.2556108,6.65177631 28.5691877,6.41659368 C28.8827645,6.18141105 29.2871136,6.06381974 29.7822349,6.06381974 C30.0132915,6.06381974 30.2299071,6.08238679 30.4320816,6.11952089 C30.6342562,6.15665499 30.8116746,6.22473417 30.964337,6.32375843 C31.1169994,6.4227827 31.2366538,6.55687805 31.3233,6.7260445 C31.4099462,6.89521095 31.4532693,7.11182653 31.4532693,7.37589123 C31.4532693,7.54918369 31.4305763,7.68946807 31.3851902,7.79674435 C31.339804,7.90402064 31.2758509,7.98860386 31.1933306,8.05049403 C31.1108104,8.11238419 31.0076601,8.15570731 30.8838798,8.18046337 C30.7600995,8.20521944 30.6156891,8.22584949 30.4506487,8.24235354 C30.0297955,8.29186567 29.6151314,8.34344081 29.2066564,8.39707895 C28.7981813,8.45071709 28.4309663,8.54355234 28.1050114,8.67558469 C27.7790566,8.80761704 27.5170549,8.99947655 27.3190063,9.25116322 C27.1209578,9.50284989 27.0219335,9.85149782 27.0219335,10.297107 C27.0219335,10.6189359 27.0776347,10.8974416 27.189037,11.1326242 C27.3004393,11.3678069 27.4531017,11.5617294 27.6470242,11.7143918 C27.8409467,11.8670542 28.0678773,11.9784565 28.327816,12.0485987 C28.5877547,12.1187408 28.8662605,12.1538119 29.1633332,12.1538119 Z M29.2252234,11.5101542 C29.0436789,11.5101542 28.8662605,11.4812722 28.692968,11.423508 C28.5196755,11.3657438 28.3670131,11.2811606 28.2349808,11.1697583 C28.1029484,11.058356 27.9977351,10.9263237 27.9193409,10.7736613 C27.8409467,10.6209989 27.8017496,10.4456434 27.8017496,10.2475949 C27.8017496,9.93401804 27.8945849,9.69264639 28.0802554,9.52347994 C28.2659259,9.35431349 28.5031715,9.22434415 28.7919923,9.13357191 C29.080813,9.04279967 29.4005789,8.97472048 29.7512898,8.92933436 C30.1020007,8.88394824 30.4423966,8.83237311 30.7724775,8.77460895 C30.8797538,8.75810491 31.0035341,8.72922283 31.1438185,8.68796272 C31.2841029,8.64670261 31.3790011,8.58068644 31.4285133,8.4899142 L31.4532693,8.4899142 L31.4532693,9.5049129 C31.4532693,9.81848973 31.3934422,10.0990585 31.2737879,10.3466191 C31.1541335,10.5941798 30.9932191,10.8046064 30.7910446,10.9778988 C30.58887,11.1511913 30.3516244,11.2832236 30.0793077,11.3739959 C29.806991,11.4647681 29.5222962,11.5101542 29.2252234,11.5101542 Z M36.3549704,14.5180162 C36.8748478,14.5180162 37.3142679,14.447874 37.6732309,14.3075897 C38.0321938,14.1673053 38.3230776,13.9651308 38.5458822,13.7010661 C38.7686868,13.4370014 38.9316642,13.1151725 39.0348145,12.7355795 C39.1379648,12.3559865 39.1895399,11.9310073 39.1895399,11.4606421 L39.1895399,5.59345449 L38.4097238,5.59345449 L38.4097238,6.69509942 L38.3849678,6.69509942 C38.3106996,6.52180696 38.2054863,6.35676652 38.0693279,6.1999781 C37.9331696,6.04318969 37.7784442,5.90496832 37.6051517,5.785314 C37.4318592,5.66565968 37.2399997,5.57282444 37.0295732,5.50680826 C36.8191466,5.44079208 36.594279,5.407784 36.3549704,5.407784 C35.8928571,5.407784 35.4823191,5.49236722 35.1233561,5.66153367 C34.7643931,5.83070012 34.4631943,6.06175674 34.2197597,6.35470352 C33.976325,6.64765029 33.7906546,6.99010921 33.6627482,7.38208025 C33.5348419,7.77405129 33.4708887,8.18871539 33.4708887,8.62607256 C33.4708887,9.07993376 33.5245268,9.5069759 33.6318031,9.90719897 C33.7390794,10.307422 33.9103089,10.65607 34.1454915,10.9531427 C34.3806741,11.2502155 34.6798099,11.4833352 35.0428989,11.6525016 C35.4059878,11.8216681 35.843345,11.9062513 36.3549704,11.9062513 C36.8253356,11.9062513 37.2358737,11.786597 37.5865846,11.5472883 C37.9372956,11.3079797 38.2034233,10.9902768 38.3849678,10.5941798 L38.4097238,10.5941798 L38.4097238,11.4606421 C38.4097238,12.2363322 38.2488094,12.8304777 37.9269806,13.2430788 C37.6051517,13.6556799 37.0811483,13.8619805 36.3549704,13.8619805 C36.1404178,13.8619805 35.9299912,13.8392874 35.7236907,13.7939013 C35.5173901,13.7485152 35.3296566,13.67631 35.1604902,13.5772857 C34.9913237,13.4782615 34.8469134,13.3482921 34.727259,13.1873777 C34.6076047,13.0264633 34.5271475,12.8263517 34.4858874,12.5870431 L33.7060713,12.5870431 C33.7308274,12.925376 33.8236626,13.2162598 33.9845771,13.4596944 C34.1454915,13.7031291 34.347666,13.9032406 34.5911007,14.060029 C34.8345353,14.2168174 35.1089151,14.3323457 35.4142399,14.4066139 C35.7195647,14.4808821 36.0331415,14.5180162 36.3549704,14.5180162 Z M36.3549704,11.2502155 C35.9918814,11.2502155 35.6783046,11.1780103 35.4142399,11.0336 C35.1501752,10.8891896 34.9314966,10.6993931 34.7582041,10.4642104 C34.5849117,10.2290278 34.4570053,9.9587741 34.3744851,9.65344929 C34.2919649,9.34812448 34.2507048,9.03454764 34.2507048,8.71271879 C34.2507048,8.38263791 34.2899019,8.05874605 34.3682961,7.7410432 C34.4466903,7.42334036 34.5725336,7.14070861 34.7458261,6.89314795 C34.9191186,6.64558729 35.1377971,6.44547576 35.4018618,6.29281335 C35.6659265,6.14015094 35.9836294,6.06381974 36.3549704,6.06381974 C36.7263114,6.06381974 37.0419512,6.14221395 37.3018899,6.29900237 C37.5618286,6.45579078 37.7743181,6.66209133 37.9393586,6.91790401 C38.104399,7.17371669 38.2240533,7.45841145 38.2983215,7.77198829 C38.3725897,8.08556512 38.4097238,8.39914195 38.4097238,8.71271879 C38.4097238,9.03454764 38.3664007,9.34812448 38.2797545,9.65344929 C38.1931083,9.9587741 38.0631389,10.2290278 37.8898465,10.4642104 C37.716554,10.6993931 37.5020014,10.8891896 37.2461887,11.0336 C36.9903761,11.1780103 36.6933033,11.2502155 36.3549704,11.2502155 Z M41.4547199,14.3323457 L41.4547199,10.7674723 L41.479476,10.7674723 C41.5619962,10.9985289 41.6795875,11.2007034 41.8322499,11.3739959 C41.9849123,11.5472883 42.1602678,11.6916987 42.3583163,11.807227 C42.5563649,11.9227553 42.7709174,12.0094016 43.0019741,12.0671657 C43.2330307,12.1249299 43.4640873,12.1538119 43.6951439,12.1538119 C44.1820132,12.1538119 44.6049293,12.0651027 44.9638923,11.8876842 C45.3228552,11.7102658 45.621991,11.4668311 45.8612997,11.1573803 C46.1006083,10.8479295 46.2780268,10.4889665 46.3935551,10.0804914 C46.5090834,9.67201634 46.5668475,9.24084819 46.5668475,8.78698699 C46.5668475,8.33312578 46.5090834,7.90195763 46.3935551,7.49348254 C46.2780268,7.08500746 46.1006083,6.7260445 45.8612997,6.41659368 C45.621991,6.10714286 45.3228552,5.8616452 44.9638923,5.68010072 C44.6049293,5.49855624 44.1820132,5.407784 43.6951439,5.407784 C43.1752665,5.407784 42.7110903,5.52950132 42.3026152,5.77293597 C41.8941401,6.01637062 41.5991303,6.36089253 41.4175858,6.80650172 L41.3928298,6.80650172 L41.3928298,5.59345449 L40.6749039,5.59345449 L40.6749039,14.3323457 L41.4547199,14.3323457 Z M43.6951439,11.5101542 C43.2825428,11.5101542 42.9338949,11.429697 42.6492001,11.2687826 C42.3645054,11.1078682 42.1334487,10.8995046 41.9560303,10.6436919 C41.7786118,10.3878792 41.6507055,10.0969955 41.5723113,9.7710406 C41.493917,9.44508574 41.4547199,9.11706786 41.4547199,8.78698699 C41.4547199,8.42389802 41.489791,8.0773131 41.5599332,7.74723222 C41.6300754,7.41715134 41.7517927,7.12833057 41.9250852,6.88076992 C42.0983776,6.63320926 42.3294343,6.43516073 42.618255,6.28662433 C42.9070758,6.13808794 43.2660388,6.06381974 43.6951439,6.06381974 C44.0664849,6.06381974 44.3841877,6.14427696 44.6482524,6.30519138 C44.9123171,6.46610581 45.1289327,6.67446937 45.2980992,6.93028205 C45.4672656,7.18609473 45.5910459,7.4769785 45.6694401,7.80293337 C45.7478344,8.12888824 45.7870315,8.45690611 45.7870315,8.78698699 C45.7870315,9.11706786 45.7478344,9.44508574 45.6694401,9.7710406 C45.5910459,10.0969955 45.4672656,10.3878792 45.2980992,10.6436919 C45.1289327,10.8995046 44.9123171,11.1078682 44.6482524,11.2687826 C44.3841877,11.429697 44.0664849,11.5101542 43.6951439,11.5101542 Z M48.5720889,4.3927853 L48.5720889,3.14260397 L47.7922728,3.14260397 L47.7922728,4.3927853 L48.5720889,4.3927853 Z M48.5720889,11.9805195 L48.5720889,5.59345449 L47.7922728,5.59345449 L47.7922728,11.9805195 L48.5720889,11.9805195 Z M52.6320837,12.1538119 C53.4407818,12.1538119 54.0658725,11.9557634 54.5073557,11.5596664 C54.9488388,11.1635693 55.2521006,10.6024318 55.4171411,9.87625388 L54.637325,9.87625388 C54.5135447,10.3713752 54.2907401,10.7674723 53.9689112,11.064545 C53.6470824,11.3616178 53.2014732,11.5101542 52.6320837,11.5101542 C52.2607427,11.5101542 51.9430398,11.43176 51.6789751,11.2749716 C51.4149104,11.1181832 51.1962319,10.9201347 51.0229394,10.680826 C50.8496469,10.4415174 50.7238036,10.1712637 50.6454094,9.87006487 C50.5670152,9.56886607 50.5278181,9.27798229 50.5278181,8.99741355 L55.4418971,8.99741355 C55.4584012,8.55180436 55.4171411,8.11238419 55.3181168,7.67915304 C55.2190926,7.24592189 55.0561151,6.86220287 54.8291845,6.52799598 C54.6022539,6.19378909 54.3072441,5.92353537 53.9441552,5.71723482 C53.5810662,5.51093427 53.143709,5.407784 52.6320837,5.407784 C52.1287103,5.407784 51.6934162,5.50474525 51.3262012,5.69866777 C50.9589862,5.89259029 50.6598504,6.14840297 50.4287938,6.46610581 C50.1977372,6.78380866 50.0265077,7.14483462 49.9151054,7.54918369 C49.8037031,7.95353277 49.748002,8.36613387 49.748002,8.78698699 C49.748002,9.24084819 49.8037031,9.67201634 49.9151054,10.0804914 C50.0265077,10.4889665 50.1977372,10.8479295 50.4287938,11.1573803 C50.6598504,11.4668311 50.9589862,11.7102658 51.3262012,11.8876842 C51.6934162,12.0651027 52.1287103,12.1538119 52.6320837,12.1538119 Z M54.6620811,8.3413778 L50.5278181,8.3413778 C50.5608262,8.05255703 50.6289053,7.76992528 50.7320556,7.49348254 C50.8352059,7.21703981 50.9734273,6.97360516 51.1467197,6.7631786 C51.3200122,6.55275204 51.5304387,6.38358559 51.7779994,6.25567925 C52.0255601,6.12777291 52.3102548,6.06381974 52.6320837,6.06381974 C52.9456605,6.06381974 53.2262293,6.12777291 53.4737899,6.25567925 C53.7213506,6.38358559 53.9317771,6.55275204 54.1050696,6.7631786 C54.278362,6.97360516 54.4124574,7.2149768 54.5073557,7.48729353 C54.6022539,7.75961025 54.653829,8.04430501 54.6620811,8.3413778 Z" id="magpie" fillRule="nonzero"></path>
                                      <g id="icon" transform="translate(0.000000, 1.850649)">
                                          <path d="M1.28333745,2.67454004 L2.84842813,3.57203553 L2.84562887,3.67957302 L2.84562887,7.69731249 C2.84562887,9.97953559 4.71168495,11.8295273 7.01337572,11.8295273 C7.1547383,11.8295273 7.29435134,11.8222426 7.43221485,11.8087136 C6.27787039,11.6560798 5.38770601,10.6764479 5.38770601,9.49041345 C5.38770601,8.53645182 5.96400347,7.71604483 6.7901348,7.35215183 L6.7901348,7.35215183 L6.7901348,7.69731249 C6.7901348,9.84290582 8.43913944,11.6064714 10.5494313,11.8099401 C9.50269693,12.6060644 8.21127656,13.0957093 6.81002668,13.1452005 L6.57467532,13.1493506 C2.94358241,13.1493506 0,10.1983797 0,6.57467532 C0,5.11456746 0.47715567,3.76562625 1.28333745,2.67454004 Z M6.57467532,0 C10.1268314,0 13.0210439,2.82406247 13.1452005,6.33932397 L13.1493506,6.57467532 C13.1493506,6.95510203 13.1169591,7.32798222 13.0547959,7.69072138 L7.64390882,5.73804876 L6.94304433,3.14500762 C6.69356037,2.22122587 5.85063348,1.61207801 4.92967725,1.6131187 C4.16058082,1.6131187 3.48875864,2.0266177 3.12765422,2.64096899 L3.12765422,2.64096899 L1.30930502,2.63963655 C2.46576727,1.09585206 4.28445171,0.0767268136 6.33932397,0.00415014281 L6.57467532,0 Z M4.35722877,2.73532446 C4.5671732,2.73532446 4.73722819,2.9039155 4.73722819,3.11205258 C4.73722819,3.32018966 4.5671732,3.4887807 4.35722877,3.4887807 C4.14728434,3.4887807 3.97722935,3.32018966 3.97722935,3.11205258 C3.97722935,2.9039155 4.14728434,2.73532446 4.35722877,2.73532446 Z" id="Combined-Shape"></path>
                                      </g>
                                  </g>
                                </g>
                              </svg>
                            </span>
                          </a>
                        </Link>
                      </div>
                      <div className="p-1 md:p-0 md:pl-4">
                        <Link href="https://magpie.im/terms.html" passHref>
                          <a className="mr-3 bg-transparent no-underline cursor-pointer text-blue-500 focus:outline-none focus:ring-0" target="_blank" rel="noopener">
                            <span className={cn("m-0 text-xs font-normal", {
                              "text-gray-400": isMobile || !ColorUtils.isDark(brandColor),
                              "text-isDark-gray-500": !isMobile && ColorUtils.isDark(brandColor)
                            })}>Terms</span>
                          </a>
                        </Link>
                        <Link href="https://magpie.im/privacy.html" passHref>
                          <a className="bg-transparent no-underline cursor-pointer text-blue-500 focus:outline-none focus:ring-0" target="_blank" rel="noopener">
                            <span className={cn("m-0 text-xs font-normal", {
                              "text-gray-400": isMobile || !ColorUtils.isDark(brandColor),
                              "text-isDark-gray-500": !isMobile && ColorUtils.isDark(brandColor)
                            })}>Privacy</span>
                          </a>
                        </Link>
                      </div>
                    </footer>
                  </div>
                </>
              ) : (
                <div>
                  <div className="absolute flex items-center justify-center inset-0">
                    <div>
                      <div className="flex justify-center flex-col max-w-75 text-center">
                        <div className="mb-4">
                          <h1 className="m-0 text-4xl font-semibold text-gray-800">Can't open link</h1>
                        </div>
                        <div className="mb-3">
                          <p className="mb-2 text-13 font-normal text-gray-500">The payment link you tried is no longer active.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home;
